/**
 * RetentionList - Fila de decisão de e-mails (Plano B, Task 8)
 *
 * Migrada para o Design System (padrão-lista, ref `UsersPage`):
 *  - Desktop: tabela tokenizada (`var(--theme-*)`) dentro de um card arredondado.
 *  - Mobile: cards empilhados (mobile-first).
 * Colunas: De / Assunto+snippet / Canal / Recebido em / Anexos / Status. Clique
 * na linha (ou no card) seleciona a entry e abre o `RetentionReadingPane`.
 */

import React from 'react';
import type { EmailRetentionResponse, EmailRetentionStatus } from '@troia-v3/types';

import { StatusPill, CardActionsMenu, PlainHeadCell, SelectAllHeadCell } from '@/shared/components/ui';
import type { CardAction, StatusPillVariant } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

export type RetentionRowAction = 'approve' | 'trust' | 'discard' | 'block';

export interface RetentionListProps {
  entries: EmailRetentionResponse[];
  /** Mapa `channelId -> nome do canal`, resolvido pelo dropdown de canais. */
  channelNameById: Record<string, string>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Seleção em massa (#154). */
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
  /** ••• por linha (#155). */
  onRowAction: (action: RetentionRowAction, entry: EmailRetentionResponse) => void;
  canApprove: boolean;
  canBlock: boolean;
}

/* F62: pill de status via StatusPill shared (sem borda, dot currentColor). */
const STATUS_BADGE: Record<EmailRetentionStatus, { variant: StatusPillVariant; label: string }> = {
  pending: { variant: 'warning', label: 'Pendente' },
  approved: { variant: 'success', label: 'Aprovado' },
  blocked: { variant: 'error', label: 'Bloqueado' },
  discarded: { variant: 'neutral', label: 'Descartado' },
};

/** Checkbox custom 18px (mesmo do padrão de bulk de TicketsPage/LeadsListPage). */
const Cbx: React.FC<{ on: boolean; mixed?: boolean; onClick: () => void; testid?: string }> = ({ on, mixed, onClick, testid }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    className="inline-flex items-center justify-center flex-none transition-colors"
    style={{
      width: 18, height: 18, borderRadius: 5,
      border: `1.5px solid ${on || mixed ? 'var(--theme-primary)' : 'var(--theme-input-border)'}`,
      backgroundColor: on || mixed ? 'var(--theme-primary)' : 'var(--theme-card-background)',
      color: on || mixed ? '#fff' : 'transparent',
    }}
    data-testid={testid}
    aria-label="Selecionar"
  >
    <Icon name={mixed ? 'minus' : 'check'} size={12} />
  </button>
);

/** Ações do ••• por linha (só quando pending + conforme permissão). */
function buildRowActions(
  entry: EmailRetentionResponse,
  onRowAction: RetentionListProps['onRowAction'],
  canApprove: boolean,
  canBlock: boolean,
): CardAction[] {
  if (entry.status !== 'pending') return [];
  const actions: CardAction[] = [];
  if (canApprove) {
    actions.push({ label: 'Aprovar', icon: 'check', onClick: () => onRowAction('approve', entry) });
    actions.push({ label: 'Confiar', icon: 'user-check', onClick: () => onRowAction('trust', entry) });
  }
  if (canBlock) {
    actions.push({ label: 'Descartar', icon: 'x-circle', onClick: () => onRowAction('discard', entry), dividerBefore: true });
    actions.push({ label: 'Bloquear', icon: 'shield', onClick: () => onRowAction('block', entry), variant: 'danger' });
  }
  return actions;
}

function formatReceivedAt(value: Date | string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Divide `from` ("Nome <email>" ou só "email") em nome exibível + e-mail. */
function parseFrom(from: string): { name: string; email: string } {
  const trimmed = (from ?? '').trim();
  const angle = trimmed.match(/^"?([^"<]*)"?\s*<([^>]+)>$/);
  if (angle && angle[1] && angle[1].trim() && angle[2]) {
    return { name: angle[1].trim(), email: angle[2].trim() };
  }
  const local = trimmed.split('@')[0] ?? trimmed;
  return { name: local || trimmed || '—', email: trimmed || '—' };
}

/** Iniciais (até 2 letras) para o avatar SLATE — apenas derivado do `from`. */
function getInitials(name: string): string {
  const cleaned = name.replace(/[._\-]+/g, ' ').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const firstPart = parts[0] ?? '';
  const lastPart = parts.length > 1 ? (parts[parts.length - 1] ?? '') : firstPart;
  const a = firstPart.charAt(0);
  const b = parts.length > 1 ? lastPart.charAt(0) : firstPart.charAt(1);
  return (a + b).toUpperCase() || '?';
}

const AvatarFallback: React.FC<{ name: string; size: number }> = ({ name, size }) => (
  <div
    className="rounded-[32%] flex items-center justify-center text-white font-bold flex-none"
    style={{ width: size, height: size, backgroundColor: '#64748b', fontSize: size <= 34 ? 12 : 14 }}
    aria-hidden
  >
    {getInitials(name)}
  </div>
);

export const RetentionList: React.FC<RetentionListProps> = ({
  entries,
  channelNameById,
  selectedId,
  onSelect,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  onRowAction,
  canApprove,
  canBlock,
}) => {
  const allSelected = entries.length > 0 && entries.every((e) => selectedIds.has(e.id));
  const someSelected = entries.some((e) => selectedIds.has(e.id));
  return (
    <div data-testid="retention-list">
      {/* Mobile: cards */}
      <div className="grid grid-cols-1 gap-3 md:hidden">
        {entries.map((entry) => {
          const isSelected = entry.id === selectedId;
          const { name, email } = parseFrom(entry.from);
          const status = STATUS_BADGE[entry.status];
          const rowActions = buildRowActions(entry, onRowAction, canApprove, canBlock);
          return (
            <div
              key={entry.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(entry.id)}
              data-testid={`retention-card-${entry.id}`}
              className="w-full text-left rounded-xl p-3 transition-colors cursor-pointer"
              style={{
                backgroundColor: 'var(--theme-card-background)',
                border: `1px solid ${isSelected ? 'var(--theme-primary)' : 'var(--theme-card-border)'}`,
              }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Cbx on={selectedIds.has(entry.id)} onClick={() => onToggleSelect(entry.id)} testid={`retention-check-${entry.id}`} />
                <AvatarFallback name={name} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-bold truncate" style={{ color: 'var(--theme-text)' }}>
                    {name}
                  </div>
                  <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                    {email}
                  </div>
                </div>
                <StatusPill variant={status.variant} label={status.label} data-testid={`retention-status-${entry.id}`} />
                {rowActions.length > 0 && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <CardActionsMenu actions={rowActions} size="sm" data-testid={`retention-row-actions-${entry.id}`} />
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>
                {entry.subject || '(Sem assunto)'}
              </p>
              {entry.snippet && (
                <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                  {entry.snippet}
                </p>
              )}
              <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                <span className="truncate">{channelNameById[entry.channelId] || 'Canal'}</span>
                <span aria-hidden>·</span>
                <span className="whitespace-nowrap">{formatReceivedAt(entry.email.receivedAt)}</span>
                {entry.attachmentsCount > 0 && (
                  <span className="ml-auto inline-flex items-center gap-1 whitespace-nowrap">
                    <Icon name="paperclip" className="h-3.5 w-3.5" />
                    {entry.attachmentsCount}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop: tabela */}
      <div
        className="hidden md:block rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
      >
        <table className="w-full border-collapse">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
              {/* F43/F59: head cells shared — sem affordance de sort (a listagem não ordena) */}
              <SelectAllHeadCell
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onToggle={onToggleAll}
                data-testid="retention-select-all"
              />
              {/* ordem do mockup: De / Assunto / Canal / Anexos / Recebido em / Status */}
              <PlainHeadCell label="De" />
              <PlainHeadCell label="Assunto" />
              <PlainHeadCell label="Canal" />
              <PlainHeadCell label="Anexos" />
              <PlainHeadCell label="Recebido em" />
              <PlainHeadCell label="Status" align="right" />
              <PlainHeadCell className="w-14" />
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => {
              const isSelected = entry.id === selectedId;
              const status = STATUS_BADGE[entry.status];
              const { name, email } = parseFrom(entry.from);
              const rowActions = buildRowActions(entry, onRowAction, canApprove, canBlock);
              return (
                <tr
                  key={entry.id}
                  data-testid={`retention-row-${entry.id}`}
                  onClick={() => onSelect(entry.id)}
                  className="cursor-pointer transition-colors"
                  style={{
                    borderTop: index > 0 ? '1px solid var(--theme-card-border)' : undefined,
                    backgroundColor: isSelected ? 'var(--theme-primary-background)' : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td className="pl-4 pr-1 py-3" onClick={(e) => e.stopPropagation()}>
                    <Cbx on={selectedIds.has(entry.id)} onClick={() => onToggleSelect(entry.id)} testid={`retention-check-${entry.id}`} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5 min-w-0 max-w-[240px]">
                      <AvatarFallback name={name} size={34} />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: 'var(--theme-text)' }}>
                          {name}
                        </div>
                        <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                          {email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="min-w-0 max-w-md">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>
                        {entry.subject || '(Sem assunto)'}
                      </p>
                      {entry.snippet && (
                        <p className="text-xs truncate mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                          {entry.snippet}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                      {channelNameById[entry.channelId] || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {entry.attachmentsCount > 0 ? (
                      <span className="inline-flex items-center gap-1 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                        <Icon name="paperclip" className="h-3.5 w-3.5" />
                        {entry.attachmentsCount}
                      </span>
                    ) : (
                      <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>
                      {formatReceivedAt(entry.email.receivedAt)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <StatusPill variant={status.variant} label={status.label} data-testid={`retention-status-desktop-${entry.id}`} />
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {rowActions.length > 0 && (
                      <CardActionsMenu actions={rowActions} size="sm" data-testid={`retention-row-actions-desktop-${entry.id}`} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

RetentionList.displayName = 'RetentionList';
